import { useNavigate, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { useTelegram } from '../hooks/useTelegram'
import { usePavelUnread } from '../hooks/usePavelUnread'

export interface TabBarTab {
  icon: LucideIcon
  label: string
  path: string
  accent: string
  dot: string
  /** Optional click handler. If provided, called instead of navigating to `path`.
   * Useful for tabs that open a modal/sheet rather than route. */
  onClick?: () => void
  /** Optional override of "active" detection (used for action tabs that never highlight). */
  forceInactive?: boolean
  /** Три тарифа: раздел под замком — вкладка видна, с замком, экран под ней размыт. */
  locked?: boolean
}

interface TabBarProps {
  tabs: TabBarTab[]
  unreadComments: number
  diaryBadge: boolean
}

export function TabBar({ tabs, unreadComments, diaryBadge }: TabBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectionHaptic } = useTelegram()
  const pavelUnread = usePavelUnread()

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '88%',
        maxWidth: 348,
        height: 64,
        borderRadius: 32,
        background: 'rgba(8,18,80,0.62)',
        border: '1px solid rgba(255,255,255,0.13)',
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset, 0 8px 28px rgba(5,10,50,0.40)',
        display: 'flex',
        alignItems: 'center',
        zIndex: 300,
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.forceInactive
          ? false
          : tab.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(tab.path)

        const showUnreadBadge = tab.path === '/diary' && unreadComments > 0
        const showRedDot = tab.path === '/diary' && unreadComments === 0 && diaryBadge && !isActive
        // На иконке бота — точка-индикатор непрочитанного сообщения от Pavel.
        const showPavelDot = tab.path === '__pavel' && pavelUnread

        const navKey = tab.path === '/' ? 'home' : tab.path.split('/')[1]

        return (
          <button
            key={tab.path}
            data-testid={`nav-${navKey}`}
            data-active={isActive ? 'true' : 'false'}
            data-locked={tab.locked ? 'true' : undefined}
            onClick={() => {
              selectionHaptic()
              if (tab.onClick) tab.onClick()
              else navigate(tab.path)
            }}
            style={{
              flex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              position: 'relative',
              padding: 0,
            }}
          >
            <div style={{ position: 'relative' }}>
              <tab.icon
                size={22}
                color={isActive ? 'white' : 'rgba(255,255,255,0.36)'}
                strokeWidth={isActive ? 2.4 : 1.5}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  transition: 'color 0.18s, transform 0.18s',
                  transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                  filter: isActive ? `drop-shadow(0 0 7px ${tab.dot})` : 'none',
                }}
              />
              {showUnreadBadge && (
                <div style={{
                  position: 'absolute', top: -6, right: -10,
                  minWidth: 16, height: 16, borderRadius: 8,
                  padding: '0 5px',
                  background: '#FF3B30',
                  color: 'white',
                  fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 6px rgba(255,59,48,0.55)',
                  lineHeight: 1,
                  zIndex: 2,
                }}>
                  {unreadComments > 99 ? '99+' : unreadComments}
                </div>
              )}
              {showRedDot && (
                <div style={{
                  position: 'absolute', top: -2, right: -4,
                  width: 6, height: 6, borderRadius: 3,
                  background: '#FF3B30',
                  boxShadow: '0 0 4px rgba(255,59,48,0.6)',
                  zIndex: 2,
                }} />
              )}
              {tab.locked && (
                <div aria-hidden style={{
                  position: 'absolute', top: -7, right: -9,
                  fontSize: 10, lineHeight: 1, zIndex: 2,
                  filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.5))',
                }}>🔒</div>
              )}
              {showPavelDot && (
                <div style={{
                  position: 'absolute', top: -3, right: -5,
                  width: 9, height: 9, borderRadius: '50%',
                  background: '#FF3B30',
                  border: '1.5px solid rgba(255,255,255,0.95)',
                  boxShadow: '0 0 6px rgba(255,59,48,0.7)',
                  zIndex: 2,
                }} />
              )}
            </div>

            <div
              style={{
                width: isActive ? 16 : 4,
                height: 3,
                borderRadius: 2,
                background: isActive ? tab.accent : 'transparent',
                marginTop: 4,
                boxShadow: isActive ? `0 0 8px ${tab.dot}` : 'none',
                transition: 'width 0.22s cubic-bezier(0.34,1.56,0.64,1), background 0.18s, box-shadow 0.18s',
              }}
            />

            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: isActive ? 'rgba(255,255,255,0.88)' : 'transparent',
                lineHeight: 1,
                marginTop: 2,
                maxHeight: isActive ? 12 : 0,
                overflow: 'hidden',
                transition: 'max-height 0.2s ease, color 0.15s',
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
              }}
            >
              {tab.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
